import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Button, message, Popover,
} from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useGetActiveUserReportsQuery } from '@services/userreport/userreport.service';
import { UserReportState } from '@services/userreport/types';
import ReportExportPopover from './ReportExportPopover';
import { ACTIVE_REPORT_STATES, reportFileName } from './utils';

const POLLING_INTERVAL = 5000;

const ReportExportIndicator = () => {
  const { t } = useTranslation();
  const [pollingInterval, setPollingInterval] = useState(0);
  const previousStates = useRef<Record<number, UserReportState>>({});
  const initialized = useRef(false);
  const { data, isFetching } = useGetActiveUserReportsQuery(undefined, {
    pollingInterval,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    setPollingInterval(data?.pollingRequired ? POLLING_INTERVAL : 0);
  }, [data?.pollingRequired]);

  useEffect(() => {
    if (!data) return;

    const currentStates = Object.fromEntries(
      data.userReports.map((report) => [report.id, report.state]),
    );
    if (initialized.current) {
      data.userReports.forEach((report) => {
        const previousState = previousStates.current[report.id];
        if (!previousState || !ACTIVE_REPORT_STATES.includes(previousState)) return;
        if (report.state === 'ready') {
          message.success(t('exportReports.finished', {
            name: reportFileName(report),
          }));
        } else if (report.state === 'failed') {
          message.error(t('exportReports.failed', { name: reportFileName(report) }));
        }
      });
    }
    previousStates.current = currentStates;
    initialized.current = true;
  }, [data, t]);

  return (
    <Popover
      placement="bottomRight"
      trigger="click"
      content={(
        <ReportExportPopover
          reports={data?.userReports || []}
          loading={isFetching && !data}
        />
      )}
    >
      <Badge count={data?.activeCount || 0} size="small">
        <Button type="text" aria-label={t('exportReports.title')} icon={<BellOutlined />} />
      </Badge>
    </Popover>
  );
};

export default ReportExportIndicator;
